import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiRequest } from "src/async/apiUtils";
import BrowserEmbedError from "src/components/embedTypes/BrowserEmbedError";
import ContentError from "src/components/embedTypes/ContentError";
import ContentLoading from "src/components/embedTypes/ContentLoading";
import ContentTypesRouter from "src/components/embedTypes/ContentTypesRouter";
import { WorkspaceContext } from "src/context/workspace";
import { useWorkspaceSwitching } from "src/context/WorkspaceSwitchingContext";
import { classNames } from "src/helpers/classNames";
import { getNavigateLink } from "src/helpers/userHelper";
import useBrowserType from "src/hooks/useBrowserType";
import useCheckThirdPartyCookies from "src/hooks/useCheckThirdPartyCookies";
import useUrlQueryParams from "src/hooks/useUrlQueryParams";
import Maintenance from "src/components/shared/Maintenance";
import DocumentEmbedModal from "./DocumentEmbedModal";
import DocumentRadialMenu from "src/components/shared/DocumentRadialMenu";
import { AuthContext } from "src/context/auth";
import RefreshTimer from "./RefreshTimer";
import { useSiteSettings } from "src/context/SiteSettingsContext";
import useSocketListeners from "src/hooks/useSocketListeners";
import useDomoFilterPersistence from "src/hooks/useDomoFilterPersistence";

const Dashboard = ({ header_menu = false }) => {
  const { loading, details: workspaceDetails, menuType, setWorspacePaymentModalOpen = () => { }, params, menusLoaded } = useContext(WorkspaceContext);
  const { site } = useSiteSettings();
  const { user } = useContext(AuthContext);

  const { workspace_id, content_type, content_value } = useParams();
  const { group, page, form, document } = useUrlQueryParams();
  const { finishSwitching } = useWorkspaceSwitching();

  const { thirdPartyCookiesEnabled, cookieCheckComplete } = useCheckThirdPartyCookies(workspaceDetails);
  const navigate = useNavigate();
  const browser = useBrowserType();

  const [isHeaderMenu, setIsHeaderMenu] = useState(header_menu);
  const [loadingPage, setLoadingPage] = useState(true);
  const [content, setContent] = useState(null);
  const [contentError, setContentError] = useState(null);
  const [browserError, setBrowserError] = useState(null);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [altLoader, setAltLoader] = useState(false);

  const [documentModalOpen, setDocumentModalOpen] = useState(false);
  const [activeDocumentTab, setActiveDocumentTab] = useState("view");

  const [isRefreshing, setIsRefreshing] = useState(false);

  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [loadingUploadedDocuments, setLoadingUploadedDocuments] = useState(false);

  const onTokenExpiredRef = useRef();

  const { getStorageKey } = useDomoFilterPersistence();

  const pageErrorTypes = {
    page_error: {
      title: "Page Error",
      subtitle: "Oops... Something went wrong while loading the page.",
      secondary: "Please try refreshing the page or come back later.",
    },
    payment_error: {
      title: "Payment Issue",
      subtitle: "We're having trouble with the payment system.",
      secondary: "Please try again later or contact support if the issue persists.",
    },
    incorrect_credentials: {
      title: "Login Error",
      subtitle: "Oops... The credentials entered are incorrect.",
      secondary: "Please double-check your username and password, and try again.",
    },
    incorrect_filters: {
      title: "Filter Error",
      subtitle: "Oops... The filters applied are invalid or incomplete.",
      secondary: "Please verify the filters and try again.",
    },
  };

  const pageUpdateCondition = (data) => {
    if (data?.content_type === "form" && data?.content_id === content?.data?.form?._id) {
      return true;
    }
  };

  const handlePageGet = async (signal, isBackground = false, prefetch = false) => {
    try {
      if (!isHeaderMenu) {
        localStorage.setItem("workspace", workspace_id || "");
        localStorage.setItem("group", group || "");
        localStorage.setItem("page", page || "");
      }

      if (!prefetch) {
        if (!isBackground) {
          setLoadingPage(true);
          setContent(null);
        } else {
          setIsRefreshing(true);
        }

        setContentError(null);
        setMaintenanceMode(false);
      }

      let queries = {
        group,
        page,
        form,
        document,
        ...(content_type && content_value ? { [content_type]: content_value } : {}),
      };

      if (isBackground || prefetch) {
        queries.request_type = "auto_refresh";
      }

      const userFilters = JSON.parse(localStorage.getItem(getStorageKey({ page_id: params?.active_page_id })));

      if (userFilters?.filters?.length > 0) {
        queries.userFilters = JSON.stringify(userFilters?.filters);
      }

      const { data: contentResponseData } = await apiRequest(
        "GET",
        isHeaderMenu ? "/portal/header-menu/embed/:workspace_id" : `/portal/embed/:workspace_id`,
        {
          params: { workspace_id },
          queries,
        },
        { returnErrorWithstatus: true, signal, is_abort: true },
      );

      const { data } = contentResponseData;

      if (prefetch) {
        return data;
      }

      if (data?.config?.type !== "IFRAME_EMBED") finishSwitching();

      setIsRefreshing(false);

      if (data?.invalidWorkspace) {
        navigate("/permission-denied");
      } else if (data?.invalidPage || data?.invalidGroup) {
        const { data: resData } = await apiRequest("GET", `/portal/users/${workspace_id}/page`, { queries: { group } });
        const navigate_link = getNavigateLink(resData);
        navigate(navigate_link);
      } else if (data?.invalidFilter) {
        setContentError("incorrect_filters");
      } else {
        setContent(data);
        if (data?.config?.type !== "IFRAME_EMBED" || !isBackground) setLoadingPage(false);
      }
    } catch (error) {
      setIsRefreshing(false);
      if (error.message !== "canceled") {
        if (!isBackground) {
          setLoadingPage(false);
        }
        finishSwitching();
        if (error?.status === 423) {
          setMaintenanceMode(true);
          return;
        }
        if (error?.response?.data?.data?.invalidFilter) {
          setContentError("incorrect_filters");
        } else {
          if (error?.status === 402 || error?.response?.data?.status === 402) {
            setContentError("payment_error");
            navigate(`/workspace/${workspace_id}/payment-required`);
          } else if (
            (error?.status === 403 || error?.response?.data?.status === 403) &&
            ["WORKSPACE_PAUSED", "WORKSPACE_PAUSED_SCHEDULED"].includes(error?.response?.data?.code)
          ) {
            navigate(`/workspace/${workspace_id}/workspace-paused`, {
              state: {
                message: error?.response?.data?.message,
                pause_expires_at: error?.response?.data?.pause_expires_at,
              },
            });
          } else {
            setContentError("page_error");
          }
        }
      }
    }
  };

  const groupUpdateResponseCondition = (data) => {
    const isUserTeamUpdate = ["users", "teams", "role", "slug", "delete"].includes(data?.type) && data?.slug === group;
    const isPageUpdate = data?.type === "pages" && data?.slug === group && ["DOMO", "METABASE", "QUICKSIGHT", "TABLEAU", "SIGMA"].includes(params?.page_type);
    const isSlugsIncluded = data?.type === "pages" && data.changed_slugs.includes(page || form || document);

    return isUserTeamUpdate || isPageUpdate || isSlugsIncluded;
  };

  const handlePageSync = async (embed_data) => {
    if (embed_data?.invalidWorkspace) {
      navigate("/permission-denied");
    } else if (embed_data?.invalidPage || embed_data?.invalidGroup) {
      const { data: resData } = await apiRequest("GET", `/portal/users/${workspace_id}/page`);
      const navigate_link = getNavigateLink(resData);
      navigate(navigate_link);
    } else if (embed_data?.invalidFilter) {
      setContentError("incorrect_filters");
    } else {
      setContent(embed_data);
    }
  };

  useSocketListeners([
    {
      event: "filter-updated-response",
      condition: () => ["DOMO", "METABASE", "QUICKSIGHT", "TABLEAU", "SIGMA"].includes(params?.page_type),
      handler: () => handlePageGet(null, true),
      title: "Filter Changes",
      message: "The filters has been updated.",
    },
    {
      event: "user-permission-update-response",
      condition: (data) => data?.workspace_slug === workspace_id && data?.changed_slugs?.includes(page),
      handler: () => handlePageGet(null, true),
    },
    {
      event: "group-update-response",
      condition: groupUpdateResponseCondition,
      preFetch: true,
      title: "Page Updated",
      message: "Your page details has changed, sync now",
      handler: () => handlePageGet(null, true, true),
      onSync: handlePageSync,
    },
    {
      event: "page-update-response",
      condition: pageUpdateCondition,
      preFetch: true,
      title: "Page Updated",
      message: "Your page details has changed, sync now",
      handler: () => handlePageGet(null, true, true),
      onSync: handlePageSync,
    },
    {
      event: "page-delete-response",
      condition: (data) => data?.content_id === params?.active_page_id,
      preFetch: true,
      title: "Page Deleted",
      message: "Your page details has changed, sync now",
      handler: () => handlePageGet(null, true, true),
      onSync: handlePageSync,
    },
  ]);

  const handleRetryAfterMaintenance = () => {
    setMaintenanceMode(false);
    handlePageGet();
  };

  const getUploadedDocuments = async () => {
    if (!user?._id) return;
    try {
      const queries = {
        group,
        page,
        form,
        document,
        keyword: "",
        ...(content_type && content_value ? { [content_type]: content_value } : {}),
      };
      setUploadedDocuments([]);
      setLoadingUploadedDocuments(true);
      const { data } = await apiRequest("get", "portal/workspaces/:workspace_id/documents", { params: { workspace_id }, queries });
      setUploadedDocuments(data?.data);
    } catch (error) {
    } finally {
      setLoadingUploadedDocuments(false);
    }
  };

  useEffect(() => {
    getUploadedDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group, page, form, document, workspace_id, isHeaderMenu, content_type, content_value]);

  useEffect(() => {
    setIsHeaderMenu(header_menu);
  }, [header_menu]);

  useEffect(() => {
    const controller = new AbortController();

    if (workspace_id && (params?.active_page_id || menusLoaded)) {
      setWorspacePaymentModalOpen(false);
      handlePageGet(controller.signal);
    }

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group, page, form, document, workspace_id, isHeaderMenu, content_type, content_value, params?.active_page_id, menusLoaded]);

  useEffect(() => {
    onTokenExpiredRef.current = () => {
      const controller = new AbortController();
      handlePageGet(controller.signal, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, group, workspace_id, content_type, content_value]);

  useEffect(() => {
    if (cookieCheckComplete) {
      if (!browser) return;
      if (content?.config?.type !== "JWT_FULL_EMBED") return;

      const shouldShowBrowserError = () => {
        if (!thirdPartyCookiesEnabled) return "3PartyCookiesNotEnabled";
        if (!browser.includes("Chrome", "Edge")) return "NotUsingChromeOrEdge";
        return false;
      };

      let errorMessage = shouldShowBrowserError();
      setBrowserError(errorMessage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cookieCheckComplete, browser, thirdPartyCookiesEnabled]);

  useEffect(() => {
    const handleTokenExpired = () => {
      if (onTokenExpiredRef.current) {
        onTokenExpiredRef.current();
      }
    };

    window.addEventListener("metabase:token-expired", handleTokenExpired);
    return () => {
      window.removeEventListener("metabase:token-expired", handleTokenExpired);
    };
  }, []);

  const sigmaFetchedAtRef = useRef(Date.now());
  const sigmaRefreshRef = useRef();

  useEffect(() => {
    sigmaFetchedAtRef.current = Date.now();
    sigmaRefreshRef.current = () => handlePageGet(null, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  // Sigma embed URLs carry a ~5 minute JWT, so refetch when the user returns to a stale tab
  useEffect(() => {
    if (params?.page_type !== "SIGMA") return;

    const onVisible = () => {
      if (window.document.visibilityState === "visible" && Date.now() - sigmaFetchedAtRef.current > 4 * 60 * 1000) {
        sigmaFetchedAtRef.current = Date.now();
        sigmaRefreshRef.current?.();
      }
    };

    window.document.addEventListener("visibilitychange", onVisible);
    return () => window.document.removeEventListener("visibilitychange", onVisible);
  }, [params?.page_type]);

  useEffect(() => {
    const ports = {};
    const handleMessage = (event) => {
      if (!event.ports[0]) return;
      const referenceId = event.data.referenceId;
      const port = event.ports[0];
      port.start();
      const onPortMessage = () => { };

      port.onmessage = onPortMessage;
      ports[referenceId] = port;
    };

    const onIFrameLoaded = (e) => {
      finishSwitching();
      setLoadingPage(false);
    };

    window.addEventListener("message", handleMessage, { passive: true });
    window.addEventListener("domo:iframe-loaded", onIFrameLoaded, { passive: true });

    return () => {
      window.removeEventListener("message", handleMessage);
      window.removeEventListener("domo:iframe-loaded", onIFrameLoaded);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finishSwitching, loading]);

  useEffect(() => {
    setAltLoader(params?.page_type === "METABASE" ? true : false);
  }, [params?.page_type]);

  return (
    <>
      <div className={classNames("relative flex h-full w-full", loadingPage && "h-[calc(100vh-4rem)] overflow-hidden")}>
        {contentError || browserError ? (
          browserError ? (
            <BrowserEmbedError
              browser={browser}
              cookiesEnabled={thirdPartyCookiesEnabled}
            />
          ) : (
            <ContentError
              pageErrorTypes={pageErrorTypes}
              pageError={contentError}
              refresh={() => handlePageGet()}
            />
          )
        ) : maintenanceMode ? (
          <Maintenance onRetry={handleRetryAfterMaintenance} />
        ) : (
          content?.config?.provider && (
            <div className="relative flex w-full flex-grow flex-col">
              <ContentTypesRouter
                content={content}
                menuType={menuType}
                workspace={workspaceDetails}
                site={site}
                setError={() => { }}
                setLoading={() => { }}
              />
            </div>
          )
        )}

        {!loadingPage && !loadingUploadedDocuments && (
          <DocumentRadialMenu
            content={content}
            documentModalOpen={documentModalOpen}
            setDocumentModalOpen={setDocumentModalOpen}
            setActiveDocumentTab={setActiveDocumentTab}
            page_type={params?.page_type}
            uploadedDocuments={uploadedDocuments}
          />
        )}

        {loadingPage && (
          <ContentLoading
            menuType={menuType}
            overlay={true}
            altLoader={altLoader}
          />
        )}

        <RefreshTimer
          enabled={content?.data?.auto_refresh_enabled}
          timeframe={content?.data?.auto_refresh_timeframe}
          onRefresh={handlePageGet}
          isRefreshing={isRefreshing}
        />
      </div>
      <DocumentEmbedModal
        content={content}
        documentModalOpen={documentModalOpen}
        setDocumentModalOpen={setDocumentModalOpen}
        activeTab={activeDocumentTab}
        setActiveTab={setActiveDocumentTab}
      />
    </>
  );
};

export default Dashboard;
